<template>
  <v-app>
    <v-container fluid class="production-reports-page">
      <v-card variant="outlined" class="reports-shell">
        <div class="reports-heading d-flex flex-wrap align-center ga-3 pa-4">
          <v-icon icon="mdi-chart-box-multiple-outline" size="large" color="primary" />
          <div>
            <h2 class="text-h6 font-weight-bold">Production Reports Center</h2>
            <div class="text-caption text-medium-emphasis">Requisition → Material Issues → Production → Finished Goods → Inventory</div>
          </div>
          <v-spacer />
          <v-chip color="primary" variant="tonal" size="small">{{ catalog.length }} Reports</v-chip>
        </div>

        <v-card-text class="pa-4">
          <v-row dense class="mb-3">
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="reportKey" :items="catalog" item-title="title" item-value="key"
                label="Select Production Report" density="compact" variant="outlined" hide-details
                prepend-inner-icon="mdi-file-chart-outline" @update:model-value="changeReport"
              >
                <template #item="{ props: option, item }">
                  <v-list-item v-bind="option" :subtitle="item.raw.group" />
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="6" md="2"><v-text-field v-model="filters.from_date" type="date" label="From Date" density="compact" variant="outlined" hide-details clearable /></v-col>
            <v-col cols="6" md="2"><v-text-field v-model="filters.to_date" type="date" label="To Date" density="compact" variant="outlined" hide-details clearable /></v-col>
            <v-col cols="12" md="3"><v-text-field v-model="filters.search" label="Search Code / Product / Batch" density="compact" variant="outlined" hide-details clearable prepend-inner-icon="mdi-magnify" @keyup.enter="load(1)" /></v-col>
            <v-col v-if="supportsItemType" cols="12" md="3">
              <v-select v-model="filters.item_type" label="Material Type" :items="[{title:'All',value:''},{title:'Raw Material (RM)',value:'raw_material'},{title:'Packaging (PM)',value:'packaging'}]" item-title="title" item-value="value" density="compact" variant="outlined" hide-details />
            </v-col>
          </v-row>

          <div class="d-flex flex-wrap align-center ga-2 mb-3">
            <v-btn color="primary" prepend-icon="mdi-magnify" size="small" :loading="loading" @click="load(1)">Generate Report</v-btn>
            <v-btn variant="outlined" size="small" prepend-icon="mdi-refresh" @click="resetFilters">Reset Filters</v-btn>
            <v-spacer />
            <v-btn color="success" variant="tonal" size="small" prepend-icon="mdi-file-delimited-outline" :disabled="!reportKey" :loading="exporting" @click="downloadCsv">Export CSV · All Rows</v-btn>
            <v-btn variant="tonal" size="small" prepend-icon="mdi-printer-outline" :disabled="rows.length === 0" @click="printReport">Print Current Page</v-btn>
          </div>

          <div v-if="catalog.length" class="d-flex align-center ga-2 flex-wrap mb-3">
            <span class="text-caption text-medium-emphasis">Category</span>
            <v-chip
              v-for="group in groups" :key="group" size="small"
              :color="group === activeGroup ? 'primary' : 'default'"
              :variant="group === activeGroup ? 'flat' : 'outlined'"
              @click="pickGroup(group)"
            >{{ group }}</v-chip>
          </div>

          <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" closable class="mb-3" @click:close="errorMessage = ''">
            {{ errorMessage }}
          </v-alert>

          <div class="report-summary d-flex align-center flex-wrap ga-3 pa-3 mb-3">
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold">{{ currentReport?.title || 'Production Report' }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ currentReport?.group || '' }} · Posted/Approved workflow figures; requisition reports include draft/pending records
              </div>
            </div>
            <v-chip variant="tonal" size="small">Total Rows: {{ meta.total.toLocaleString() }}</v-chip>
            <v-chip variant="tonal" size="small">Page {{ meta.current_page }} / {{ meta.last_page }}</v-chip>
          </div>

          <div class="report-table-scroll">
            <v-table density="compact" class="production-report-table" fixed-header height="min(60vh, 650px)">
              <thead>
                <tr>
                  <th class="index-col">#</th>
                  <th v-for="column in columns" :key="column.key" :class="{ 'text-end': numericColumn(column.key) }">{{ column.title }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading"><td :colspan="columns.length + 1" class="text-center pa-8"><v-progress-circular indeterminate color="primary" size="25" /></td></tr>
                <tr v-else-if="!rows.length"><td :colspan="columns.length + 1" class="text-center text-medium-emphasis py-8">No records found for this report and filter.</td></tr>
                <tr v-for="(row, idx) in (loading ? [] : rows)" :key="idx">
                  <td class="text-medium-emphasis index-col">{{ (meta.current_page - 1) * meta.per_page + idx + 1 }}</td>
                  <td v-for="col in columns" :key="col.key" :class="{ 'text-end numeric-cell': numericColumn(col.key) }">
                    {{ displayValue(row[col.key], col.key) }}
                  </td>
                </tr>
                <tr v-if="!loading && rows.length && costColumns.length" class="page-subtotal">
                  <td class="font-weight-bold">Page Total</td>
                  <td v-for="col in columns" :key="col.key" :class="{ 'text-end': costColumns.some((cost) => cost.key === col.key) }">
                    {{ costColumns.some((cost) => cost.key === col.key) ? pageTotal(col.key) : '' }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <div class="d-flex justify-space-between align-center ga-2 flex-wrap mt-3">
            <div class="text-caption text-medium-emphasis">CSV includes every matching row. Screen/print shows the current page; no mixing of Kg, Pcs or other units.</div>
            <div class="d-flex align-center ga-2">
              <v-select v-model="filters.per_page" :items="[25, 50, 100, 200]" label="Rows" density="compact" variant="outlined" hide-details style="width:105px" @update:model-value="load(1)" />
              <v-btn icon="mdi-chevron-left" variant="outlined" size="small" :disabled="loading || meta.current_page <= 1" @click="load(meta.current_page-1)" />
              <span class="text-caption">{{ meta.current_page }} / {{ meta.last_page }}</span>
              <v-btn icon="mdi-chevron-right" variant="outlined" size="small" :disabled="loading || meta.current_page >= meta.last_page" @click="load(meta.current_page+1)" />
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Axios from 'axios';
import { prefix } from '@/store/api';
import { toast } from 'vue3-toastify';

const base = `${prefix}prd/reports`;
const route = useRoute();
const router = useRouter();
const catalogReady = ref(false);
const catalog = ref([]);
const reportKey = ref('production_register');
const rows = ref([]);
const loading = ref(false);
const exporting = ref(false);
const errorMessage = ref('');
const filters = reactive({ from_date: '', to_date: '', search: '', item_type: '', per_page: 50 });
const meta = reactive({ total: 0, current_page: 1, last_page: 1, per_page: 50 });
const currentReport = computed(() => catalog.value.find((r) => r.key === reportKey.value));
const supportsItemType = computed(() => ['purity_trace', 'bom_variance', 'planned_loss', 'requisition_materials', 'material_issues', 'material_receipts', 'production_rm_stock', 'rm_expiry'].includes(reportKey.value));
const columns = computed(() => Object.entries(currentReport.value?.columns || {}).map(([key, title]) => ({ key, title })));
const costColumns = computed(() => columns.value.filter((col) => /^(amount|material_cost|packaging_cost|labour_cost|overhead_cost|total_cost|balance_value|cost)$/.test(col.key)));
const groups = computed(() => [...new Set(catalog.value.map((r) => r.group))]);
const activeGroup = computed(() => currentReport.value?.group || 'Production');

function params() {
  return {
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
    search: filters.search?.trim() || undefined,
    item_type: supportsItemType.value ? (filters.item_type || undefined) : undefined,
    per_page: filters.per_page,
  };
}

function numericColumn(key) {
  return /(?:_qty|_quantity|_cost|_value|_percent|_purity|_rate|_remaining|_loss|_price|_total|_percent|^amount$|^quantity$|^rate$|^received_qty$|^consumed_qty$|^unit_cost$|^days_remaining$|^production_count$|^formula_qty$)/.test(key);
}

function displayValue(value, key) {
  if (value === null || value === undefined || value === '') return '—';
  if (/^\d{4}-\d{2}-\d{2}/.test(String(value)) && /(date|expiry|manufactured)/i.test(key)) return String(value).slice(0, 10);
  if (numericColumn(key) && !Number.isNaN(Number(value))) {
    const precision = key === 'unit_cost' || key === 'rate' ? 6 : /(cost|amount|value)/.test(key) ? 2 : 4;
    return Number(value).toLocaleString('en-US', { maximumFractionDigits: precision });
  }
  return String(value);
}

function pageTotal(key) {
  return rows.value.reduce((total, row) => total + (Number(row[key]) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

async function load(page = 1) {
  if (!reportKey.value || (filters.from_date && filters.to_date && filters.to_date < filters.from_date)) {
    errorMessage.value = 'Select a report and valid date range.';
    return;
  }
  loading.value = true;
  errorMessage.value = '';
  try {
    const response = await Axios.get(`${base}/${reportKey.value}`, { params: { ...params(), page } });
    rows.value = response.data.data || [];
    Object.assign(meta, response.data.meta || { total: 0, current_page: 1, last_page: 1, per_page: filters.per_page });
  } catch (error) {
    rows.value = [];
    errorMessage.value = error.response?.data?.message || 'Could not load production report.';
  } finally {
    loading.value = false;
  }
}

function changeReport() {
  if (!reportKey.value) return;
  // Update the URL so direct sidebar selection and the dropdown stay in sync.
  const destination = `/production/reports/${reportKey.value}`;
  if (route.path !== destination) {
    router.push({ path: destination });
  } else {
    rows.value = [];
    filters.item_type = '';
    load(1);
  }
}

watch(
  () => [route.params.reportKey, route.query.report],
  () => {
    if (!catalogReady.value) return;
    const requested = String(route.params.reportKey || route.query.report || 'production_register');
    if (!catalog.value.some((report) => report.key === requested)) {
      errorMessage.value = 'The selected report is unavailable.';
      return;
    }
    if (reportKey.value === requested) return;
    reportKey.value = requested;
    filters.item_type = '';
    rows.value = [];
    load(1);
  },
);

function pickGroup(group) {
  const first = catalog.value.find((report) => report.group === group);
  if (first) { reportKey.value = first.key; changeReport(); }
}
function resetFilters() {
  Object.assign(filters, { from_date: '', to_date: '', search: '', item_type: '', per_page: 50 });
  load(1);
}

async function downloadCsv() {
  exporting.value = true;
  try {
    const response = await Axios.get(`${base}/${reportKey.value}/export`, { params: params(), responseType: 'blob' });
    const file = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `production_${reportKey.value}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error('CSV export failed. Please retry.');
  } finally { exporting.value = false; }
}

function escapeHtml(value) {
  return String(value ?? '—').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
function printReport() {
  // Explicitly print the visible page; full dataset remains available through CSV.
  const header = columns.value.map((c) => `<th>${escapeHtml(c.title)}</th>`).join('');
  const body = rows.value.map((row) => `<tr>${columns.value.map((c) => `<td>${escapeHtml(displayValue(row[c.key], c.key))}</td>`).join('')}</tr>`).join('');
  const reportHtml = `<!DOCTYPE html><html><head><title>${escapeHtml(currentReport.value?.title)}</title>
    <style>@page{size:A4 landscape;margin:11mm}body{font:10px Arial,sans-serif;color:#222}h1{font-size:17px;margin:0 0 5px}
    .sub{color:#666;margin-bottom:12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #bbb;padding:5px;text-align:left;vertical-align:top}th{background:#f0f2f4}thead{display:table-header-group}tr{break-inside:avoid}
    .note{margin-top:12px;color:#777;font-size:9px}</style></head><body><h1>${escapeHtml(currentReport.value?.title)}</h1>
    <div class="sub">Production Reports Center · ${escapeHtml(filters.from_date || 'All dates')} – ${escapeHtml(filters.to_date || 'All dates')} · Page ${meta.current_page}/${meta.last_page}</div>
    <table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table><div class="note">Only current page printed. Download CSV for all filtered rows.</div></body></html>`;
  const page = window.open('', '_blank');
  if (!page) return toast.error('Allow pop-ups to print the report.');
  page.document.write(reportHtml);
  page.document.close();
  page.focus();
  page.print();
}

onMounted(async () => {
  try {
    const response = await Axios.get(`${base}/catalog`);
    catalog.value = response.data.data || [];
    catalogReady.value = true;
    const requested = String(route.params.reportKey || route.query.report || 'production_register');
    reportKey.value = catalog.value.some((report) => report.key === requested)
      ? requested
      : (catalog.value[0]?.key || '');
    if (reportKey.value) await load(1);
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Could not load Production Reports catalog.';
  }
});
</script>

<style scoped>
.production-reports-page { max-width: 100%; }
.reports-shell { border-radius: 10px; overflow: hidden; }
.reports-heading { background: #edf2f6; border-bottom: 1px solid rgba(0,0,0,.1); }
.report-summary { background: #f8fafc; border: 1px solid #e5eaf0; border-radius: 8px; }
.report-table-scroll { overflow-x: auto; border: 1px solid #ddd; border-radius: 7px; }
.production-report-table { min-width: 900px; }
.production-report-table :deep(th) { background: #eaf0f4 !important; color: #243449; font-size: 12px; white-space: nowrap; font-weight: 700; border: 1px solid #d4dce2; }
.production-report-table :deep(td) { font-size: 12px; border-bottom: 1px solid #e9edf1; white-space: nowrap; }
.production-report-table :deep(tbody tr:hover) { background: #f7fbff; }
.production-report-table .index-col { width: 42px; }
.numeric-cell { font-variant-numeric: tabular-nums; }
.page-subtotal { font-weight: bold; background: #f4f7fa; }
@media print { .production-reports-page { display: none; } }
</style>
